import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Grid,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { brandColors } from '@config/brandTokens';
import { COUNTRIES } from '@utils/countries';
import { orgTypeLabel } from '@utils/orgTypes';
import { buildSignInPath } from '@utils/authRouting';

const academyRegistrationSchema = z.object({
  academyName: z.string().min(1, 'Name is required'),
  academyType: z.enum(['regular_school', 'sports_academy', 'college', 'corporate'], {
    required_error: 'Please select a type',
  }),
  description: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  city: z.string().optional(),
  address: z.string().optional(),
  contactFirstName: z.string().min(1, 'First name is required'),
  contactLastName: z.string().min(1, 'Last name is required'),
  contactEmail: z.string().email('Enter a valid contact email'),
  contactPhone: z.string().min(8, 'Mobile number is required'),
  adminPassword: z.string().min(6, 'Password must be at least 6 characters'),
});

type AcademyRegistrationFormData = z.infer<typeof academyRegistrationSchema>;

const AcademyRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const isCorporate = searchParams.get('type') === 'corporate';
  const incomingReturnUrl = searchParams.get('returnUrl');
  const signInPath = buildSignInPath({ returnUrl: incomingReturnUrl });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdAdminId, setCreatedAdminId] = useState('');
  const [createdAcademyName, setCreatedAcademyName] = useState('');

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<AcademyRegistrationFormData>({
    resolver: zodResolver(academyRegistrationSchema),
    defaultValues: {
      academyType: isCorporate ? 'corporate' : 'sports_academy',
      country: 'India',
    },
  });
  const selectedType = watch('academyType');

  const copyAdminId = async () => {
    try {
      await navigator.clipboard.writeText(createdAdminId);
      toast.success('Admin ID copied');
    } catch {
      toast.info(createdAdminId);
    }
  };

  const onSubmit = async (data: AcademyRegistrationFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/academies/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          academyName: data.academyName,
          academy_type: isCorporate ? 'corporate' : data.academyType,
          description: data.description || null,
          country: data.country,
          city: data.city || null,
          address: data.address || null,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          adminPassword: data.adminPassword,
          adminFirstName: data.contactFirstName,
          adminLastName: data.contactLastName,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an error. Please check the console for details.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Registration failed');
      }

      if (!result.success) {
        throw new Error(result.message || 'Registration failed');
      }

      const adminId = result.data?.admin?.adminIdCode;
      if (!adminId) {
        throw new Error('Academy was created but no Admin ID was returned.');
      }

      setCreatedAcademyName(result.data?.academy?.name || data.academyName);
      setCreatedAdminId(adminId);
      toast.success(`${orgTypeLabel(data.academyType)} registered. Save the Admin ID to sign in.`);
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (createdAdminId) {
    return (
      <AuthPageShell maxWidth="md">
        <AuthFormCard
          roleLabel={isCorporate ? 'Corporate' : 'School, College or Sports Academy'}
          title="Save your Admin ID"
          subtitle={isCorporate
            ? 'This is the login for your company. The contact email is only for communications.'
            : 'This is the login for your school, college, or academy. The contact email is only for communications.'}
          chrome
          backTo={signInPath}
        >
          <Alert severity="success" sx={{ mb: 2 }}>
            {createdAcademyName} is registered. Sign in with the Admin ID and the password you just set —
            not the contact email.
          </Alert>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: brandColors.ice,
              border: `1px solid ${brandColors.navy}22`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', color: brandColors.slate }}>
              ADMIN ID
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: 'ui-monospace, Consolas, monospace',
                fontWeight: 800,
                fontSize: '1.25rem',
                color: brandColors.navy,
                letterSpacing: '0.04em',
              }}
            >
              {createdAdminId}
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyAdminId}
            sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700 }}
          >
            Copy Admin ID
          </Button>
          <Button
            fullWidth
            variant="contained"
            size="large"
            component={RouterLink}
            to={buildSignInPath({ registered: 'academy', returnUrl: incomingReturnUrl })}
            sx={authPrimaryCtaSx}
          >
            Go to sign in
          </Button>
        </AuthFormCard>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell maxWidth="md">
      <AuthFormCard
        roleLabel={isCorporate ? 'Corporate' : 'School, College or Sports Academy'}
        title={isCorporate ? 'Corporate registration' : 'Organisation registration'}
        subtitle={isCorporate
          ? 'Register your company so employees can map themselves and appear on your public contest profile.'
          : 'Register a regular school, college, or sports academy. You will receive an Admin ID to sign in.'}
        chrome
        backTo={signInPath}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontWeight: 700 }}>
            {isCorporate ? 'Company information' : 'Organisation information'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('academyName')}
                label={
                  selectedType === 'corporate'
                    ? 'Company name *'
                    : selectedType === 'college'
                      ? 'College name *'
                      : selectedType === 'regular_school'
                        ? 'School name *'
                        : 'Academy name *'
                }
                fullWidth
                error={!!errors.academyName}
                helperText={errors.academyName?.message}
              />
            </Grid>

            <Grid item xs={12}>
              {isCorporate ? (
                <input type="hidden" {...register('academyType')} value="corporate" />
              ) : (
              <FormControl error={!!errors.academyType} sx={{ width: '100%' }}>
                <FormLabel sx={{ color: brandColors.navy, fontWeight: 700, mb: 0.5 }}>
                  Registering as *
                </FormLabel>
                <Controller
                  name="academyType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}
                      sx={{
                        gap: { xs: 1, sm: 3 },
                        flexWrap: 'wrap',
                      }}
                    >
                      <FormControlLabel
                        value="regular_school"
                        control={<Radio />}
                        label="Regular school"
                      />
                      <FormControlLabel
                        value="college"
                        control={<Radio />}
                        label="College"
                      />
                      <FormControlLabel
                        value="sports_academy"
                        control={<Radio />}
                        label="Sports academy"
                      />
                    </RadioGroup>
                  )}
                />
                {errors.academyType ? (
                  <FormHelperText>{errors.academyType.message}</FormHelperText>
                ) : (
                  <FormHelperText>
                    Colleges use U19 and U19+ contest ages. Schools and academies include U9–U19.
                  </FormHelperText>
                )}
              </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('country')}
                select
                label="Country *"
                fullWidth
                defaultValue="India"
                error={!!errors.country}
                helperText={errors.country?.message}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('city')}
                label="City"
                fullWidth
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('address')}
                label="Address"
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register('description')}
                label="Description"
                fullWidth
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontWeight: 700 }}>
            Primary contact
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: brandColors.slate }}>
            Name, mobile, and email are for EncycGames communications only. They are not used to sign in.
            After registration you get an Admin ID. EncycGames can issue more Admin IDs for the same organisation later.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('contactFirstName')}
                label="Contact first name *"
                fullWidth
                error={!!errors.contactFirstName}
                helperText={errors.contactFirstName?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('contactLastName')}
                label="Contact last name *"
                fullWidth
                error={!!errors.contactLastName}
                helperText={errors.contactLastName?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('contactEmail')}
                label="Contact email *"
                type="email"
                fullWidth
                error={!!errors.contactEmail}
                helperText={errors.contactEmail?.message || 'Used only for communications, not login'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('contactPhone')}
                label="Contact mobile *"
                fullWidth
                error={!!errors.contactPhone}
                helperText={errors.contactPhone?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('adminPassword')}
                label="Admin ID password *"
                type="password"
                fullWidth
                error={!!errors.adminPassword}
                helperText={errors.adminPassword?.message || 'Use this password with the Admin ID you will receive after registration'}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2, ...authPrimaryCtaSx }}
            disabled={isLoading}
          >
            {isLoading ? 'Registering...' : isCorporate ? 'Register company' : 'Register organisation'}
          </Button>

          <Box sx={{ mt: 1, textAlign: 'center', ...authLinkSx }}>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              Already have an Admin ID?{' '}
              <Link component={RouterLink} to={signInPath}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default AcademyRegistrationPage;
